@use '@/scss/underscore' as _;

// Default Styles
.radio {
  user-select: none;
  cursor: pointer;
  font: var(--font-body-2);

  &:not(:last-child) {
    margin-bottom: _.unit(2);
  }

  .content {
    display: flex;
    align-items: center;

    .indicator {
      border-radius: 50%;
      border: 2px solid var(--color-neutral-60);
      display: inline-block;
      margin-right: _.unit(2);

      &::before {
        content: '';
        background: var(--color-layer-1);
        width: 10px;
        height: 10px;
        display: block;
        border-radius: 50%;
        border: 2px solid var(--color-layer-1);
      }
    }

    .icon,
    .trailingIcon {
      margin-right: _.unit(2);
      color: var(--color-text-secondary);

      > svg {
        display: block;
      }
    }

    .trailingIcon {
      margin-right: unset;
      margin-left: _.unit(2);
    }
  }
}

.card {
  padding: _.unit(3);
  border-radius: _.unit(4);
  border: 1px solid transparent;
  outline: 1px solid var(--color-neutral-90);

  &:not(:last-child) {
    margin-bottom: unset;
  }

  .content {
    position: relative;
    display: block;

    .indicator {
      border-radius: unset;
      border: unset;
      display: block;
      margin-right: unset;
      position: absolute;
      right: 0;
      top: 0;

      svg {
        opacity: 0%;
      }

      &::before {
        display: none;
      }
    }

    .icon,
    .trailingIcon {
      margin-right: _.unit(2);
      vertical-align: middle;
      color: var(--color-text-secondary);

      > svg {
        display: unset;
      }
    }

    .trailingIcon {
      margin-right: unset;
      margin-left: _.unit(2);
    }

    .disabledLabel {
      background: var(--color-neutral-90);
      padding: _.unit(0.5) _.unit(2);
      border-radius: 10px;
      font: var(--font-label-3);
      color: var(--color-text);
    }
  }
}

.compact {
  position: relative;
  border: 1px solid var(--color-border);
  flex: 1;
  font: var(--font-label-2);

  &:first-child {
    border-radius: 12px 0 0 12px;
  }

  &:last-child {
    border-radius: 0 12px 12px 0;
  }

  &:not(:first-child) {
    border-left: none;
  }

  &:not(:last-child) {
    margin-bottom: unset;
  }

  .content {
    padding: _.unit(5);
    height: 100%;

    .icon {
      margin-right: _.unit(4);
    }

    .trailingIcon {
      margin-left: _.unit(4);
    }
  }
}

.small {
  position: relative;
  border: 1px solid var(--color-border);
  flex: 1;
  font: var(--font-body-2);
  height: 36px;

  &:first-child {
    border-radius: 6px 0 0 6px;
  }

  &:last-child {
    border-radius: 0 6px 6px 0;
  }

  &:not(:first-child) {
    border-left: none;
  }

  &:not(:last-child) {
    margin-bottom: unset;
  }

  .content {
    height: 100%;
    display: flex;
    justify-content: center;
  }
}

// Checked Styles
.radio.checked {
  .content {
    .indicator {
      border-color: var(--color-primary);

      &::before {
        background: var(--color-primary);
      }
    }
  }
}

.card.checked {
  border-color: var(--color-primary);
  outline: 1px solid var(--color-primary);

  .content {
    .indicator {
      svg {
        opacity: 100%;
      }
    }
  }
}

.compact.checked {
  color: var(--color-primary);
  border-color: var(--color-primary);
  background-color: var(--color-hover-variant);

  .content {
    .icon,
    .trailingIcon {
      color: var(--color-primary);
    }
  }

  &:not(:first-child)::before {
    position: absolute;
    content: '';
    width: 1px;
    top: -1px;
    left: -1px;
    bottom: -1px;
    background-color: var(--color-primary);
  }
}

.small.checked {
  color: var(--color-text-link);
  border-color: var(--color-text-link);
  background-color: var(--color-hover-variant);

  &:not(:first-child)::before {
    position: absolute;
    content: '';
    width: 1px;
    top: -1px;
    left: -1px;
    bottom: -1px;
    background-color: var(--color-text-link);
  }
}


// Disabled Styles
.radio.disabled {
  cursor: not-allowed;
  color: var(--color-disabled);

  .content {
    .indicator {
      border-color: var(--color-disabled);

      &::before {
        background: var(--color-layer-1);
      }
    }
  }
}

.plain.disabled {
  color: var(--color-text);

  .content {
    .indicator {
      border-color: var(--color-border);

      &::before {
        background: var(--color-bg-state-unselected);
        border-color: var(--color-bg-state-unselected);
      }
    }
  }
}

.card.disabled {
  background-color: var(--color-layer-2);
  border-color: var(--color-layer-2);
  outline: unset;
}

.small.disabled {
  cursor: not-allowed;
  color: var(--color-text);
  background-color: var(--color-bg-state-disabled);
}

.compact.disabled {
  cursor: not-allowed;
  background-color: var(--color-layer-2);

  .content {
    .icon,
    .trailingIcon {
      color: var(--color-text-secondary);
    }
  }
}

// Not Disabled Behaviors
.card:not(.disabled) {
  &:focus {
    outline: 1px solid var(--color-primary);
    box-shadow: var(--shadow-2);
  }

  &:hover {
    box-shadow: var(--shadow-2);
  }
}

.compact:not(.disabled) {
  &:hover {
    color: var(--color-primary);
    border-color: var(--color-primary);

    .content {
      .icon,
      .trailingIcon {
        color: var(--color-primary);
      }
    }

    &:not(:first-child)::before {
      position: absolute;
      content: '';
      width: 1px;
      top: -1px;
      left: -1px;
      bottom: -1px;
      background-color: var(--color-primary);
    }
  }
}
